import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardHeader, CardContent } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { LoadingTable } from '../../../components/Loading';
import { Input } from '@/components/Input';
import { useCustomerStore } from '../../../store/customerStore';

export function CustomerList() {
  const { customers, isLoading, fetchCustomers } = useCustomerStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  useEffect(() => {
    fetchCustomers({ search: searchTerm, type: typeFilter });
  }, [fetchCustomers, searchTerm, typeFilter]);

  if (isLoading) {
    return (
      <div>
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="tracking-tight text-foreground font-display text-page-title">Customers</h1>
            <p className="text-muted-foreground mt-1">Manage customer relationships</p>
          </div>
          <Button variant="primary">+ Add Customer</Button>
        </div>
        <LoadingTable rows={5} columns={7} />
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="tracking-tight text-foreground font-display text-page-title">Customers</h1>
          <p className="text-muted-foreground mt-1">Manage customer relationships</p>
        </div>
        <Button variant="primary">+ Add Customer</Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <Card>
          <CardContent className="p-6 text-center">
            <p className="text-sm text-muted-foreground mb-2">Total Customers</p>
            <p className="text-foreground font-display text-metric">{customers.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6 text-center">
            <p className="text-sm text-muted-foreground mb-2">Active Customers</p>
            <p className="text-success font-display text-metric">{customers.filter(c => c.status === 'active').length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6 text-center">
            <p className="text-sm text-muted-foreground mb-2">Total Invoices</p>
            <p className="text-primary font-display text-metric">{customers.reduce((sum, c) => sum + (c.invoices || 0), 0)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6 text-center">
            <p className="text-sm text-muted-foreground mb-2">Total Revenue</p>
            <p className="text-success font-display text-metric">${customers.reduce((sum, c) => sum + (c.revenue || 0), 0).toLocaleString()}</p>
          </CardContent>
        </Card>
      </div>

      {/* Customers Table */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <h2 className="text-foreground font-display text-section-title">All Customers</h2>
            <div className="flex gap-4">
              <Input
                type="text"
                placeholder="Search customers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="px-4 py-2 bg-card border border-border rounded-lg text-muted-foreground placeholder-text-40 focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <select 
                className="px-4 py-2 bg-card border border-border rounded-lg text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <option value="">All Types</option>
                <option value="enterprise">Enterprise</option>
                <option value="smb">SMB</option>
                <option value="small-business">Small Business</option>
                <option value="startup">Startup</option>
              </select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-card">
                <tr>
                  <th className="text-left py-4 px-6 text-sm font-semibold text-muted-foreground">Customer</th>
                  <th className="text-left py-4 px-6 text-sm font-semibold text-muted-foreground">Type</th>
                  <th className="text-left py-4 px-6 text-sm font-semibold text-muted-foreground">Contact</th>
                  <th className="text-left py-4 px-6 text-sm font-semibold text-muted-foreground">Invoices</th>
                  <th className="text-left py-4 px-6 text-sm font-semibold text-muted-foreground">Revenue</th>
                  <th className="text-left py-4 px-6 text-sm font-semibold text-muted-foreground">Status</th>
                  <th className="text-left py-4 px-6 text-sm font-semibold text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-12">
                {customers.map((customer) => (
                  <tr key={customer.id} className="hover:bg-card/50 transition-colors">
                    <td className="py-4 px-6">
                      <Link to={`/money/customers/${customer.id}`} className="text-primary hover:text-primary font-medium">
                        {customer.name}
                      </Link>
                      <p className="text-sm text-muted-foreground">{customer.email}</p>
                    </td>
                    <td className="py-4 px-6 text-muted-foreground">{customer.type}</td>
                    <td className="py-4 px-6 text-muted-foreground">{customer.phone}</td>
                    <td className="py-4 px-6 text-muted-foreground">{customer.invoices || 0}</td>
                    <td className="py-4 px-6 text-success font-semibold">${(customer.revenue || 0).toLocaleString()}</td>
                    <td className="py-4 px-6">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        customer.status === 'active' ? 'bg-success/20 text-success' : 'bg-card text-muted-foreground'
                      }`}>
                        {customer.status}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      <Link
                        to={`/money/customers/${customer.id}`}
                        className="text-primary hover:text-primary text-sm font-medium"
                      >
                        View →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

